/** @format */

import React, { createContext, useEffect, useReducer } from 'react';
import Keycloak from 'keycloak-js';
import SplashScreen from 'src/components/SplashScreen';
import { keycloakConfig } from 'src/config';
import axios from 'src/utils/axios';
import { ENDPOINTS } from 'src/constants';
import { useTranslation } from 'react-i18next';
import { setUserData } from 'src/slices/users';
import { useDispatch } from 'src/store';

export let keycloakClient = new Keycloak({
  ...keycloakConfig
});

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
  role: null,
  error: null,
  email: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { user, uuid, isAuthenticated } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
        uuid
      };
    }
    case 'LOGIN': {
      const { user, role, user_status, uuid, email } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        isInitialised: true,
        user,
        user_status,
        role: role,
        uuid: uuid,
        email
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    case 'ON_ERROR': {
      return {
        ...state,
        isInitialised: true,
        isAuthenticated: false,
        user: null,
        error: action.payload.error
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const storeDispatch = useDispatch();

  const {
    i18n: { language: locale }
  } = useTranslation();

  const logout = () => {
    keycloakClient.logout();
  };

  const getUserInfo = async (roles, uuid, email) => {
    const response = await axios.get(`${ENDPOINTS.GET_USER_INFO_ENDPOINT}`);
    const user = response.data;
    dispatch({
      type: 'LOGIN',
      payload: {
        user: { ...user, email },
        role: roles[0],
        uuid
      }
    });

    storeDispatch(setUserData({ ...user, email }));
  };

  useEffect(() => {
    const initialise = async () => {
      try {
        keycloakClient.onTokenExpired = async () => {
          const refreshed = await keycloakClient.updateToken();
          if (refreshed) {
            axios.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
          }
        };
        keycloakClient.onAuthRefreshError = () => {
          window.location.href = '/';
        };
        keycloakClient.onAuthError = () => {
          window.location.href = '/';
        };

        keycloakClient.onAuthSuccess = () => {};
        keycloakClient.onReady = () => {};
        keycloakClient.onAuthLogout = () => {};
        keycloakClient.onAuthRefreshSuccess = () => {};

        await keycloakClient.init({
          onLoad: 'login-required',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          redirectUri: window.location.href,
          pkceMethod: 'S256',
          locale: locale || 'en'
        });

        if (keycloakClient.authenticated) {
          axios.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
          const keyClockAccessRoles = keycloakClient.idTokenParsed.roles || [];
          getUserInfo(keyClockAccessRoles, keycloakClient.subject, keycloakClient.tokenParsed.email);
        }
      } catch (err) {
        dispatch({
          type: 'ON_ERROR',
          payload: {
            isAuthenticated: false,
            user: null,
            error: {
              message: 'Error while trying to log in, please try again',
              err
            }
          }
        });
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        logout
      }}>
      {state.isAuthenticated ? children : <></>}
    </AuthContext.Provider>
  );
};

export default AuthContext;
